import PropTypes from "prop-types";
import React from "react";

const LAT_LIMIT = 80;
const LNG_LIMIT = 180;
const BOUNDS_WORLD = {
  north: 85,
  south: -85,
  west: -360,
  east: 360,
};
const GOOGLE_MAPS_API_KEY = "AIzaSyBaDsiFJPNmfq4y5xXbC-hH8jvUygVGWj8";

class GoogleMap extends React.PureComponent {
  static get propTypes() {
    return {
      deviceStatusOnline: PropTypes.bool,
      markers: PropTypes.array.isRequired,
      maxZoom: PropTypes.number,
      minZoom: PropTypes.number,
    };
  }

  static get defaultProps() {
    return {
      minZoom: 2,
      maxZoom: 20,
    };
  }

  constructor(props) {
    super(props);

    this.state = {
      GoogleMapsAPI: undefined,
      map: undefined,
      markers: [],
    };
  }

  componentDidUpdate(prevProps) {
    const { markers } = this.props;
    if (markers !== prevProps.markers) {
      this._addMarkers(this.state.map, markers);
    }
  }

  componentDidMount() {
    const { minZoom, maxZoom } = this.props;

    this._loadGoogleMaps()
      .then((GoogleMapsAPI) => {
        if (!this.mapContainer) {
          return;
        }
        const mapOpts = {
          maxZoom,
          minZoom,
          center: new GoogleMapsAPI.LatLng(39.965033, -75.17917),
          zoom: 8,
          restriction: {
            latLngBounds: BOUNDS_WORLD,
            strictBounds: false,
          },
        };

        const map = new GoogleMapsAPI.Map(this.mapContainer, mapOpts);
        this.setState({ map, GoogleMapsAPI }, () => {
          this._addMarkers(map, this.props.markers);
        });
      })
      .catch((err) => {
        console.error("Failed to load Google Maps:", err);
      });
  }

  componentWillUnmount() {
    this._clearMarkers();
  }

  _loadGoogleMaps() {
    return new Promise((resolve, reject) => {
      if (window.google && window.google.maps) {
        resolve(window.google.maps);
        return;
      }

      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}`;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve(window.google.maps);
      script.onerror = reject;

      document.head.appendChild(script);
    });
  }

  _addMarkers(map, markers) {
    const { GoogleMapsAPI } = this.state;
    if (!map || !markers.length || !GoogleMapsAPI) {
      return;
    }

    this._clearMarkers();

    const gmarkers = [];
    const bounds = new GoogleMapsAPI.LatLngBounds();

    markers.forEach((m) => {
      const { coords, device, deviceStatus } = m;

      const { name, device_id: deviceId, network_name: networkName } = device;

      const point = new GoogleMapsAPI.LatLng(coords.lat, coords.lng);
      if (
        coords.lat > -LAT_LIMIT &&
        coords.lat < LAT_LIMIT &&
        coords.lng > -LNG_LIMIT &&
        coords.lng < LNG_LIMIT
      ) {
        bounds.extend(point);
      }

      const color = deviceStatus.color === "teal" ? "blue" : deviceStatus.color;

      const gm = new GoogleMapsAPI.Marker({
        map,
        position: point,
        icon: `https://maps.gstatic.com/mapfiles/ms2/micons/${color}.png`,
      });

      if (m.showInfoWindow) {
        const deviceLink = `/devices/${deviceId}/overview`;
        const statusColor = `text-${deviceStatus.color}`;
        const infoWindow = new GoogleMapsAPI.InfoWindow({
          padding: 0,
          content: `
            <div class="info-window">
              <div class="header">
                <dl class="">
                  <dt>Name / Network</dt>
                  <dd>${name} / ${networkName}</dd>
                  <dt>Id</dt>
                  <dd>${deviceId}</dd>
                  <dt>Status</dt>
                  <dd class=${statusColor}>${deviceStatus.label}</dd>
                </dl>
              </div>
              <a href=${deviceLink}
                class="btn btn-primary">
                View Device
              </a>
            </div>
          `,
        });

        const onMarkerClick = function () {
          infoWindow.open(map, gm);
        };

        GoogleMapsAPI.event.addListener(gm, "click", onMarkerClick);
      }

      gmarkers.push(gm);
    });

    map.fitBounds(bounds);

    this.setState({ markers: gmarkers });
  }

  _clearMarkers() {
    const { markers } = this.state;
    if (!markers) {
      return null;
    }

    markers.forEach((m) => {
      m.setMap(null);
    });
  }

  render() {
    const { deviceStatusOnline } = this.props;
    const offlineClass = deviceStatusOnline === false ? "offline" : "";

    return (
      <div
        ref={(m) => {
          this.mapContainer = m;
        }}
        className={`map-container online-component ${offlineClass}`}
      />
    );
  }
}

export { GoogleMap };
